import { memo, useState, lazy, Suspense, useMemo } from "react";
import { Calendar as CalendarIcon, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useQuery } from "@tanstack/react-query";
import type { DateFilterType } from "@/components/FiltersPanel";

const LazyCalendar = lazy(() => import("@/components/ui/calendar").then((mod) => ({ default: mod.Calendar })));

const DAY_ABBREVS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function toLocalIsoDate(date: Date): string {
  return date.toLocaleDateString("en-CA"); // YYYY-MM-DD
}

/** Returns the next `count` calendar dates starting from the day after tomorrow. */
function getUpcomingDates(count = 5): Array<{ isoDate: string; label: string }> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const results: Array<{ isoDate: string; label: string }> = [];
  const cursor = new Date(today);
  cursor.setDate(cursor.getDate() + 2); // day after tomorrow
  while (results.length < count) {
    results.push({
      isoDate: toLocalIsoDate(cursor),
      label: `${DAY_ABBREVS[cursor.getDay()]} ${cursor.getDate()}`,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return results;
}

function formatCustomDateLabel(customDate: string | null): string {
  if (!customDate) return "Pick a date";
  return new Date(`${customDate}T12:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function formatDateSummaryLabel(dateFilter: DateFilterType, customDate: string | null): string {
  switch (dateFilter) {
    case "today":
      return "Today";
    case "tomorrow":
      return "Tomorrow";
    case "weekend":
      return "Weekend";
    case "custom":
      return formatCustomDateLabel(customDate);
    default:
      return "Any date";
  }
}

/** Small superscript count badge shown on each chip. */
function CountBadge({ count }: { count: number | undefined }) {
  if (!count) return null;
  return (
    <sup
      className="ml-0.5 inline-block align-super text-[8px] font-semibold leading-none opacity-60"
      aria-label={`${count} events`}
    >
      {count > 99 ? "99+" : count}
    </sup>
  );
}

interface EventsDateBarProps {
  dateFilter: DateFilterType;
  customDate: string | null;
  onDateFilterChange: (filter: DateFilterType, customDate: string | null) => void;
  embedded?: boolean;
  /** Serialised query-string of the currently active filters (no dates), used to
   *  keep the per-date event counts in sync with the user's other active filters. */
  filterQueryString?: string;
}

function EventsDateBarInner({
  dateFilter,
  customDate,
  onDateFilterChange,
  embedded = false,
  filterQueryString = "",
}: EventsDateBarProps) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(false);

  // Fetch per-date event counts, refreshing whenever the active filters change.
  const { data: dateCounts = {} } = useQuery<Record<string, number>>({
    queryKey: ["/api/events/counts-by-date", filterQueryString],
    queryFn: async () => {
      const url = filterQueryString
        ? `/api/events/counts-by-date?${filterQueryString}`
        : "/api/events/counts-by-date";
      const res = await fetch(url);
      if (!res.ok) return {};
      return res.json();
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Derive today / tomorrow ISO strings for the standard chips
  const todayIso = useMemo(() => toLocalIsoDate(new Date()), []);
  const tomorrowIso = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return toLocalIsoDate(d);
  }, []);

  const upcomingDates = useMemo(() => getUpcomingDates(5), []);

  const chipButtonClassName =
    "inline-flex min-h-[44px] shrink-0 items-center justify-center bg-transparent p-1.5 -m-1.5 focus-visible:outline-none";
  const chipLabelClassName =
    "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-medium leading-none transition-colors focus-visible:outline-none md:px-3";
  const summaryLabel = formatDateSummaryLabel(dateFilter, customDate);

  const activeStyle = {
    border: "1.5px solid hsl(var(--green-accent-active))",
    backgroundColor: "hsl(var(--green-accent-light))",
    color: "hsl(var(--foreground))",
    boxShadow: "var(--brutal-shadow-sm)",
  };
  const inactiveStyle = {
    border: "1.5px solid var(--brutal-border-color)",
    backgroundColor: "#fbfaf6",
    color: "hsl(var(--foreground))",
    boxShadow: "var(--brutal-shadow-sm)",
  };
  const calendarInactiveStyle = {
    border: "1.5px solid #d8d1c2",
    backgroundColor: "#fbfaf6",
    color: "hsl(var(--foreground))",
    boxShadow: "var(--brutal-shadow-sm)",
  };

  const handleDateSelection = (filter: DateFilterType, nextCustomDate: string | null) => {
    onDateFilterChange(filter, nextCustomDate);
    setMobileExpanded(false);
  };

  /** Toggle a specific weekday-date chip (reuses the 'custom' + customDate mechanism). */
  const handleSpecificDateClick = (isoDate: string) => {
    const alreadyActive = dateFilter === "custom" && customDate === isoDate;
    onDateFilterChange(alreadyActive ? null : "custom", alreadyActive ? null : isoDate);
    setMobileExpanded(false);
  };

  // ────────────────────────────────────────────────────────────────
  // Mobile: collapsed summary line + horizontally-scrollable chip row
  // ────────────────────────────────────────────────────────────────
  const mobileChips = (
    <div
      id="events-date-bar-mobile-options"
      className="mt-1.5 flex gap-1.5 overflow-x-auto pb-1"
      style={{
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        scrollSnapType: "x mandatory",
      }}
      data-testid="events-date-bar-mobile-options"
    >
      {/* Today */}
      <button
        type="button"
        onClick={() => handleDateSelection(dateFilter === "today" ? null : "today", null)}
        className={cn(chipButtonClassName, "focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2")}
        style={{ scrollSnapAlign: "start", minWidth: "72px" }}
        data-testid="date-bar-pill-today"
      >
        <span className={chipLabelClassName} style={dateFilter === "today" ? activeStyle : inactiveStyle}>
          Today<CountBadge count={dateCounts[todayIso]} />
        </span>
      </button>

      {/* Tomorrow */}
      <button
        type="button"
        onClick={() => handleDateSelection(dateFilter === "tomorrow" ? null : "tomorrow", null)}
        className={cn(chipButtonClassName, "focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2")}
        style={{ scrollSnapAlign: "start", minWidth: "72px" }}
        data-testid="date-bar-pill-tomorrow"
      >
        <span className={chipLabelClassName} style={dateFilter === "tomorrow" ? activeStyle : inactiveStyle}>
          Tomorrow<CountBadge count={dateCounts[tomorrowIso]} />
        </span>
      </button>

      {/* Upcoming weekday date chips */}
      {upcomingDates.map(({ isoDate, label }) => {
        const isActive = dateFilter === "custom" && customDate === isoDate;
        return (
          <button
            key={isoDate}
            type="button"
            onClick={() => handleSpecificDateClick(isoDate)}
            className={cn(chipButtonClassName, "focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2")}
            style={{ scrollSnapAlign: "start", minWidth: "72px" }}
            data-testid={`date-bar-pill-${isoDate}`}
          >
            <span className={chipLabelClassName} style={isActive ? activeStyle : inactiveStyle}>
              {label}<CountBadge count={dateCounts[isoDate]} />
            </span>
          </button>
        );
      })}

      {/* Weekend */}
      <button
        type="button"
        onClick={() => handleDateSelection(dateFilter === "weekend" ? null : "weekend", null)}
        className={cn(chipButtonClassName, "focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2")}
        style={{ scrollSnapAlign: "start", minWidth: "72px" }}
        data-testid="date-bar-pill-weekend"
      >
        <span className={chipLabelClassName} style={dateFilter === "weekend" ? activeStyle : inactiveStyle}>
          Weekend
        </span>
      </button>

      {/* Calendar picker */}
      <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(chipButtonClassName, "focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2")}
            style={{ scrollSnapAlign: "start", minWidth: "72px" }}
            data-testid="date-bar-custom-trigger"
          >
            <span
              className={chipLabelClassName}
              style={
                dateFilter === "custom" && !upcomingDates.some(d => d.isoDate === customDate)
                  ? activeStyle
                  : calendarInactiveStyle
              }
            >
              <CalendarIcon className="h-3 w-3" />
              <span>
                {dateFilter === "custom" && !upcomingDates.some(d => d.isoDate === customDate)
                  ? formatCustomDateLabel(customDate)
                  : "Date"}
              </span>
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Suspense fallback={<div className="p-4 text-sm text-muted-foreground">Loading calendar...</div>}>
            <LazyCalendar
              mode="single"
              selected={customDate ? new Date(`${customDate}T12:00:00`) : undefined}
              onSelect={(date) => {
                if (!date) return;
                const isoDate = date.toLocaleDateString("en-CA");
                onDateFilterChange("custom", isoDate);
                setCalendarOpen(false);
                setMobileExpanded(false);
              }}
              initialFocus
            />
          </Suspense>
        </PopoverContent>
      </Popover>
    </div>
  );

  // ────────────────────────────────────────────────────────────────
  // Desktop: always-visible horizontally-scrollable chip row
  // ────────────────────────────────────────────────────────────────
  const desktopChips = (
    <div
      className="hidden items-center gap-1.5 overflow-x-auto whitespace-nowrap pb-0 sm:flex"
      style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
    >
      <h2
        id="events-date-bar-heading-desktop"
        className="shrink-0 pr-1 text-[9px] font-semibold uppercase tracking-[0.04em] text-muted-foreground"
      >
        DATE
      </h2>

      {/* Today */}
      <button
        type="button"
        onClick={() => onDateFilterChange(dateFilter === "today" ? null : "today", null)}
        className={cn(chipButtonClassName, "focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2")}
        style={{ scrollSnapAlign: "start" }}
        data-testid="date-bar-pill-desktop-today"
      >
        <span className={chipLabelClassName} style={dateFilter === "today" ? activeStyle : inactiveStyle}>
          Today<CountBadge count={dateCounts[todayIso]} />
        </span>
      </button>

      {/* Tomorrow */}
      <button
        type="button"
        onClick={() => onDateFilterChange(dateFilter === "tomorrow" ? null : "tomorrow", null)}
        className={cn(chipButtonClassName, "focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2")}
        style={{ scrollSnapAlign: "start" }}
        data-testid="date-bar-pill-desktop-tomorrow"
      >
        <span className={chipLabelClassName} style={dateFilter === "tomorrow" ? activeStyle : inactiveStyle}>
          Tomorrow<CountBadge count={dateCounts[tomorrowIso]} />
        </span>
      </button>

      {/* Upcoming weekday date chips */}
      {upcomingDates.map(({ isoDate, label }) => {
        const isActive = dateFilter === "custom" && customDate === isoDate;
        return (
          <button
            key={isoDate}
            type="button"
            onClick={() => {
              const alreadyActive = dateFilter === "custom" && customDate === isoDate;
              onDateFilterChange(alreadyActive ? null : "custom", alreadyActive ? null : isoDate);
            }}
            className={cn(chipButtonClassName, "focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2")}
            style={{ scrollSnapAlign: "start" }}
            data-testid={`date-bar-pill-desktop-${isoDate}`}
          >
            <span className={chipLabelClassName} style={isActive ? activeStyle : inactiveStyle}>
              {label}<CountBadge count={dateCounts[isoDate]} />
            </span>
          </button>
        );
      })}

      {/* Weekend */}
      <button
        type="button"
        onClick={() => onDateFilterChange(dateFilter === "weekend" ? null : "weekend", null)}
        className={cn(chipButtonClassName, "focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2")}
        style={{ scrollSnapAlign: "start" }}
        data-testid="date-bar-pill-desktop-weekend"
      >
        <span className={chipLabelClassName} style={dateFilter === "weekend" ? activeStyle : inactiveStyle}>
          Weekend
        </span>
      </button>

      {/* Calendar picker */}
      <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(chipButtonClassName, "focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2")}
            data-testid="date-bar-custom-trigger"
          >
            <span
              className={chipLabelClassName}
              style={
                dateFilter === "custom" && !upcomingDates.some(d => d.isoDate === customDate)
                  ? activeStyle
                  : calendarInactiveStyle
              }
            >
              <CalendarIcon className="h-3 w-3" />
              <span className="hidden md:inline">
                {dateFilter === "custom" && !upcomingDates.some(d => d.isoDate === customDate)
                  ? formatCustomDateLabel(customDate)
                  : "Pick a date"}
              </span>
              <span className="md:hidden">
                {dateFilter === "custom" && !upcomingDates.some(d => d.isoDate === customDate)
                  ? formatCustomDateLabel(customDate)
                  : "Date"}
              </span>
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Suspense fallback={<div className="p-4 text-sm text-muted-foreground">Loading calendar...</div>}>
            <LazyCalendar
              mode="single"
              selected={customDate ? new Date(`${customDate}T12:00:00`) : undefined}
              onSelect={(date) => {
                if (!date) return;
                const isoDate = date.toLocaleDateString("en-CA");
                onDateFilterChange("custom", isoDate);
                setCalendarOpen(false);
              }}
              initialFocus
            />
          </Suspense>
        </PopoverContent>
      </Popover>
    </div>
  );

  return (
    <section
      aria-labelledby="events-date-bar-heading"
      className={
        embedded
          ? "px-0 py-0"
          : "mb-2 rounded-[16px] border border-[#ece8de] bg-white px-3 py-1.5 lg:px-4 lg:py-1.5"
      }
      data-testid="events-date-bar"
    >
      {/* ── Mobile: toggle + horizontally-scrollable chip row ── */}
      <div className="sm:hidden">
        <button
          type="button"
          onClick={() => setMobileExpanded((current) => !current)}
          className="flex min-h-[44px] w-full items-center justify-between gap-2 rounded-[12px] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1D9E75] focus-visible:ring-offset-2"
          aria-expanded={mobileExpanded}
          aria-controls="events-date-bar-mobile-options"
          data-testid="events-date-bar-mobile-toggle"
        >
          <div className="min-w-0 inline-flex items-center gap-1.5">
            <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <p
              id="events-date-bar-heading"
              className="truncate text-[12px] font-medium text-foreground"
            >
              {summaryLabel}
            </p>
          </div>
          {mobileExpanded ? (
            <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
        </button>

        {mobileExpanded && mobileChips}
      </div>

      {/* ── Desktop: always-visible chip row ── */}
      {desktopChips}
    </section>
  );
}

export const EventsDateBar = memo(EventsDateBarInner);
